import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, MonitorCheck, Moon, Repeat } from "lucide-react";
import ThemeToggle from "@/components/ui/ThemeToggle";
import Logo from "@/components/common/Logo";
import Container from "@/components/ui/Container";
import Button from "@/components/ui/Button";
import ExeDemo from "@/components/exe-demo/ExeDemo";

// Public walkthrough of the desktop agent — the sibling of /extension.
// The extension demo can BE the product in a panel; the .exe can't, so this
// page shows the round trip instead: dashboard → the agent on your own PC.
const POINTS = [
  {
    icon: MonitorCheck,
    title: "Always on, even after a reboot",
    body: "Starts with Windows and waits in the tray. It reconnects on its own after a power cut or a dropped connection — you never have to remember to launch it.",
  },
  {
    icon: Repeat,
    title: "Learn once, replay for free",
    body: "The first run uses AI to find every element on the page. Save it, and every replay after that runs from fixed steps — no model calls, no tokens, no cost.",
  },
  {
    icon: Moon,
    title: "Runs while you sleep",
    body: "Schedule a suite for 2 AM on a locked machine. Screenshots, network logs and bug reports are waiting for you in the morning.",
  },
];

export default function DesktopAgent() {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-ink-950">
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl dark:border-white/10 dark:bg-ink-950/80">
        <Container className="flex h-16 items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-600 dark:text-slate-300"
            >
              <ArrowLeft size={16} /> Back
            </Link>
            <ThemeToggle />
          </div>
        </Container>
      </header>

      <Container className="py-10 sm:py-14">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3.5 py-1.5 text-xs font-semibold text-brand-700 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-300">
            Desktop agent · guided walkthrough
          </span>
          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Your PC becomes the test lab
          </h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            Install the agent once. Send a test from the dashboard and watch it open its own
            browser on your machine, run the steps, catch the bug, and save the run so the
            next one costs nothing.
          </p>
        </div>

        <div className="mt-9">
          <ExeDemo />
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {POINTS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft dark:border-white/10 dark:bg-ink-900"
            >
              <span className="grid h-10 w-10 place-items-center rounded-xl border border-brand-200 bg-brand-50 text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
                <Icon size={18} />
              </span>
              <h2 className="mt-3.5 font-display text-base font-bold text-slate-900 dark:text-white">
                {title}
              </h2>
              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Button size="lg" to="/signup">
            Start testing free
            <ArrowRight size={18} />
          </Button>
          <Button size="lg" variant="outline" to="/extension">
            <Download size={18} />
            Try the browser extension
          </Button>
        </div>

        <p className="mx-auto mt-8 max-w-xl text-center text-xs text-slate-400">
          This is a scripted walkthrough of a real feature — a marketing page can&apos;t drive
          a machine in your office. Sign in to the dashboard, download the agent from the
          Exe tab, and the same screens run against your own site.
        </p>
      </Container>
    </div>
  );
}
